import React from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { UPSLabel, FedExLabel, DHLLabel } from './ShippingLabels';

// Hidden component for rendering labels for PDF generation
export const LabelRenderer = ({ data, carrier, onPDFGenerated }) => {
  const labelRef = React.useRef(null);

  React.useEffect(() => {
    if (data && carrier && labelRef.current) {
      // Small delay to ensure the component is fully rendered including barcodes
      setTimeout(() => {
        generatePDF();
      }, 1000); // Increased delay for barcode rendering
    }
  }, [data, carrier]);

  const generatePDF = async () => {
    if (!labelRef.current) return;

    try {
      // Capture the label as a canvas with better settings
      const canvas = await html2canvas(labelRef.current, {
        scale: 3, // Higher resolution for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        removeContainer: false,
        imageTimeout: 0,
        // Remove fixed dimensions to let it capture the actual element size
      });

      // Create PDF with proper dimensions
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [canvas.width / 3, canvas.height / 3] // Adjust for scale
      });

      // Add the canvas image to PDF
      const imgData = canvas.toDataURL('image/png', 1.0); // Max quality
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 3, canvas.height / 3);

      // Generate blob and open in new tab (like the original buttons)
      const pdfBlob = pdf.output('blob');
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');

      if (onPDFGenerated) {
        onPDFGenerated(true);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      if (onPDFGenerated) {
        onPDFGenerated(false, error.message);
      }
    }
  };

  const renderLabel = () => {
    switch (carrier) {
      case 'UPS':
        return <UPSLabel data={data} />;
      case 'FedEx':
        return <FedExLabel data={data} />;
      case 'DHL':
        return <DHLLabel data={data} />;
      default:
        return <UPSLabel data={data} />;
    }
  };

  return (
    <div 
      ref={labelRef}
      style={{
        position: 'absolute',
        left: '-9999px',
        top: '-9999px',
        width: '288px', // 4 inches at 72 DPI
        height: '432px', // 6 inches at 72 DPI
        backgroundColor: 'white',
        fontFamily: 'Arial, sans-serif',
        overflow: 'visible' // Ensure nothing gets cut off
      }}
    >
      {renderLabel()}
    </div>
  );
};

// Hook for generating realistic label PDFs
export const useRealisticLabelPDF = () => {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [labelData, setLabelData] = React.useState(null);
  const [carrier, setCarrier] = React.useState(null);

  const generateRealisticLabelPDF = (data, selectedCarrier) => {
    setIsGenerating(true);
    setLabelData(data);
    setCarrier(selectedCarrier);
  };

  const handlePDFGenerated = (success, error) => {
    setIsGenerating(false);
    setLabelData(null);
    setCarrier(null);
    
    if (success) {
      return { success: true };
    } else {
      return { success: false, error };
    }
  };

  return {
    isGenerating,
    labelData,
    carrier,
    generateRealisticLabelPDF,
    handlePDFGenerated,
    LabelRenderer: labelData && carrier ? 
      <LabelRenderer 
        data={labelData} 
        carrier={carrier} 
        onPDFGenerated={handlePDFGenerated} 
      /> : null
  };
}; 